import { useState } from "react";
import { motion } from "framer-motion";
import { Rocket, Package, FileText, QrCode, Truck, Wallet, Headset, TrendingUp } from "lucide-react";
import { SectionHeading, fadeUp, GlassCard } from "./GlassCard";

const metrics = [
  { label: "QR Scans", value: 128, bars: [40, 55, 48, 70, 85, 78, 100] },
  { label: "Orders Initiated", value: 64, bars: [30, 45, 60, 52, 75, 68, 90] },
  { label: "UPI Payments", value: 52, bars: [25, 40, 50, 65, 58, 80, 95] },
];

const periods = ["Today", "This Week", "This Month"];

const features = [
  { icon: Rocket, title: "Zero-Code Setup", desc: "Apna digital shop ready in just 2 minutes — no developer, no designer needed." },
  { icon: Package, title: "Unlimited Products", desc: "List unlimited products with photos, prices, discounts and stock levels." },
  { icon: FileText, title: "GST-Ready Invoices", desc: "Automatic GST invoices, orders and payments managed in one clean flow." },
  { icon: Truck, title: "Inter-City Shipping", desc: "Shiprocket integration ships your products beyond your city, pan-India." },
  { icon: Wallet, title: "Gromza Wallet", desc: "One wallet for ads, shipping and subscriptions. Recharge ₹100 to ₹1,00,000." },
  { icon: Headset, title: "Dedicated Support", desc: "Har step par aapke business ka saathi — tickets and direct WhatsApp help." },
];

const VolumetricBars = ({ bars, delay }) => (
  <div className="flex items-end gap-1.5 h-24 [transform-style:preserve-3d] [transform:rotateX(18deg)]">
    {bars.map((h, i) => (
      <motion.div
        key={i}
        initial={{ height: 0 }}
        whileInView={{ height: `${h}%` }}
        viewport={{ once: true }}
        transition={{ delay: delay + i * 0.07, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 rounded-t-sm relative"
        style={{
          background: "linear-gradient(to top, rgba(251,191,36,0.25), #fbbf24)",
          boxShadow: "0 0 14px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
        }}
      />
    ))}
  </div>
);

export const CommandCenter = () => {
  const [period, setPeriod] = useState("Today");

  return (
    <section id="command-center" data-testid="command-center-section" className="relative py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          testId="command-center-heading"
          eyebrow="Merchant Command Center"
          title="Realtime analytics,"
          highlight="volumetric view."
          description="A floating spatial dashboard for your dukaan — scans, orders and payments rendered live, with conversion front and centre."
          align="center"
        />

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          className="mt-16 [perspective:1200px]"
        >
          <div
            data-testid="analytics-dashboard"
            className="glass-panel rounded-3xl p-7 sm:p-10 max-w-5xl mx-auto border-amber-400/20 shadow-[0_40px_100px_rgba(0,0,0,0.6),0_0_80px_rgba(251,191,36,0.07)] [transform:rotateX(4deg)]"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-9">
              <div>
                <h3 className="font-display text-2xl font-semibold">Realtime analytics</h3>
                <p className="text-xs text-slate-500 mt-1">Track your catalogue performance</p>
              </div>
              <div className="flex gap-2" data-testid="analytics-period-tabs">
                {periods.map((p) => (
                  <button
                    key={p}
                    data-testid={`period-tab-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setPeriod(p)}
                    className={`rounded-full px-5 py-2 text-xs font-semibold transition-[background-color,color,border-color] duration-300 ${
                      period === p
                        ? "bg-amber-400 text-[#0b0f19]"
                        : "border border-white/10 bg-white/[0.04] text-slate-300 hover:border-amber-400/40"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {metrics.map((m, i) => (
                <div
                  key={m.label}
                  data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl bg-white/[0.03] border border-white/[0.07] p-6"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-sm text-slate-300">{m.label}</span>
                    <span className="font-display text-2xl font-semibold text-white">{m.value}</span>
                  </div>
                  <VolumetricBars bars={m.bars} delay={i * 0.15} />
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-6 flex flex-wrap items-center justify-between gap-4" data-testid="conversion-rate-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                  <TrendingUp size={22} className="text-amber-400" />
                </div>
                <div>
                  <p className="font-display text-lg font-semibold">Conversion Rate</p>
                  <p className="text-xs text-slate-400">Orders / total visits · {period.toLowerCase()}</p>
                </div>
              </div>
              <p className="font-display text-5xl font-semibold gold-text" data-testid="conversion-rate-value">20%</p>
            </div>
          </div>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {features.map((f, i) => (
            <GlassCard key={f.title} testId={`command-feature-${i}`} delay={i % 3} className="p-6 group">
              <f.icon size={20} className="text-amber-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="font-display text-base font-semibold mb-2">{f.title}</h3>
              <p className="text-[13px] text-slate-400 leading-relaxed">{f.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};
